import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, Filter } from "lucide-react";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getManagerEmployees } from "../../../redux/features/timesheet/api";
import { ManagerTimesheetEmployee } from "../../../types";
import { convertDecimalHoursToHM } from "../../../utils/employeeTimeCardhelper";
import Select from "react-select";
import { NgwAvatar } from "../../common";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";

// Mock data for UI development until API is fully integrated
const mockPayPeriods = [
    { value: "2025-05-26 - 2025-06-01", label: "May 26 - Jun 1, 2025" },
    { value: "2025-06-02 - 2025-06-08", label: "Jun 2 - Jun 8, 2025" },
    { value: "2025-06-09 - 2025-06-15", label: "Jun 9 - Jun 15, 2025" },
];

const ManagerTimesheetList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { setSourcePath } = useNavigationHistory();

    // Get data from Redux store
    const { employeeRecords, isFetchingEmployees } = useSelector((state: RootState) => state.timesheet);
    const user = useSelector((state: RootState) => state.user.user);

    // Local state for filters and selected pay period
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>(mockPayPeriods[0].value);

    // Get saved filters from localStorage or use defaults
    const defaultFilters = {
        departmentCodes: [] as string[],
        groupCodes: [] as string[],
        siteCodes: [] as string[],
        fields: ["total_hours", "total_overtime", "doubletime_hours", "total_break_minutes"] as string[],
    };

    const [filters, setFilters] = useState(() => {
        const savedFilters = localStorage.getItem("managerTimesheetFilters");
        return savedFilters ? JSON.parse(savedFilters) : defaultFilters;
    });

    useEffect(() => {
        dispatch(setTopNavNameTitle("Manager Timesheet"));
        // Load employees on component mount
        loadEmployees();
        
        // Remember where we came from (likely /mobile-dashboard)
        if (location.state?.from) {
            setSourcePath(location.state.from);
        } else {
            setSourcePath("/mobile-dashboard");
        }
        
        // Set the back handler to go to the source path
        setGlobalBackHandler(() => {
            // Navigate back to mobile dashboard or previous screen
            navigate("/mobile-dashboard");
            return true;
        });
        
        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, location.state, navigate, setSourcePath]);

    // Load employees when filters change
    useEffect(() => {
        loadEmployees();
    }, [filters, selectedPayPeriod]);

    const loadEmployees = () => {
        // Create request payload
        const payload = {
            pay_period: selectedPayPeriod,
            employee_id: "", // Empty string (preventing -1 value issue)
            department_code: filters.departmentCodes,
            group_code: filters.groupCodes,
            site_code: filters.siteCodes,
            fields: filters.fields,
        };

        // Call API to fetch employees
        dispatch(getManagerEmployees(payload)).catch((error) => {
            console.error("Error fetching manager employee data:", error);
        });
    };

    const handleEmployeeSelect = (employeeId: string) => {
        // Set the current path as source for the employee detail page
        // This ensures proper back navigation when returning from employee detail
        navigate(`/m-manager-timesheet/${employeeId}`, {
            state: {
                from: "/m-manager-timesheet"
            }
        });
    };

    const handlePayPeriodChange = (selectedOption: any) => {
        if (selectedOption) {
            setSelectedPayPeriod(selectedOption.value);
        }
    };

    const navigateToFilters = () => {
        setGlobalBackHandler(() => {
            // Navigate back to the previous screen
            navigate("/m-manager-timesheet");
        });
        navigate("/m-manager-timesheet-filters", { state: filters });
    };

    // Get any active filters count for display
    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (filters.departmentCodes.length) count++;
        if (filters.groupCodes.length) count++;
        if (filters.siteCodes.length) count++;
        return count;
    };

    return (
        <div className="container mx-auto bg-white h-full">
            <div className="flex flex-col text-gray-800 text-base font-sans">
                <div className="px-4 py-3">
                    <div className="mb-4 flex flex-col">
                        <div className="mb-3">
                            <Select
                                value={mockPayPeriods.find((p) => p.value === selectedPayPeriod)}
                                onChange={handlePayPeriodChange}
                                options={mockPayPeriods}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select Pay Period"
                            />
                        </div>

                        <button
                            onClick={navigateToFilters}
                            className="w-full py-2 px-4 border border-gray-300 rounded-md flex justify-between items-center"
                        >
                            <div className="flex items-center">
                                <Filter size={18} className="mr-2 text-gray-600" />
                                <span>Filters</span>
                                {getActiveFiltersCount() > 0 && (
                                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                                        {getActiveFiltersCount()}
                                    </span>
                                )}
                            </div>
                            <ChevronRight size={18} className="text-gray-400" />
                        </button>
                    </div>

                    {isFetchingEmployees ? (
                        <div className="flex justify-center items-center py-10">
                            <div className="h-6 w-6 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <>
                            {employeeRecords && employeeRecords.length > 0 ? (
                                <div className="bg-white rounded-md shadow-sm">
                                    {employeeRecords.map((employee) => {
                                        const name = employee.emp_detail?.emp_name;
                                        const firstName = name ? name.split(" ")[0] : "";
                                        const lastName = name ? name.split(" ")[1] : "";
                                        return (
                                            <div
                                                key={employee.emp_detail?.employee_id}
                                                onClick={() =>
                                                    handleEmployeeSelect(employee.emp_detail?.employee_id || "")
                                                }
                                                className="flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer"
                                            >
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 mr-3 overflow-hidden rounded-full bg-gray-200 flex-shrink-0">
                                                        {employee.emp_detail?.profile_image_url ? (
                                                            <img
                                                                src={employee.emp_detail.profile_image_url}
                                                                alt={`${firstName} ${lastName}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <NgwAvatar name={employee?.emp_detail?.emp_name} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">
                                                            {firstName} {lastName}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {employee.department?.name || "No Department"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="mr-2 font-medium">
                                                        {employee.total_hours
                                                            ? convertDecimalHoursToHM(parseFloat(employee.total_hours))
                                                            : "0h 0m"}
                                                    </div>
                                                    <ChevronRight size={18} className="text-gray-400" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-gray-500">
                                    No employee data found. Try changing your filters.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManagerTimesheetList;
